import { useCallback, useEffect, useState } from "react";
import api, { money, moneyCents, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { PdfPreviewModal } from "./PdfPreviewModal";
import { Loader2, Eye, Download, Send, FileText, Clock, MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";

function parseCc(raw) {
  return (raw || "")
    .split(/[,\s;]+/)
    .map((x) => x.trim())
    .filter((x) => x.includes("@"));
}

function ResendDialog({ pre, borrowerName, open, onClose, showEstimate }) {
  const [cc, setCc] = useState("");
  const [includeEstimate, setIncludeEstimate] = useState(showEstimate && !!pre?.generate_loan_estimate);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCc("");
      setIncludeEstimate(showEstimate && !!pre?.generate_loan_estimate);
    }
  }, [open, pre, showEstimate]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/preapprovals/${pre.id}/resend`, {
        cc_emails: parseCc(cc),
        include_estimate: includeEstimate,
      });
      if (data.email_status && data.email_status.preapproval_sent === false) {
        toast.error("We couldn't send that email — please check the addresses and try again.");
        return;
      }
      const ccNote = data.cc && data.cc.length ? ` (cc ${data.cc.join(", ")})` : "";
      toast.success(`Sent to ${data.to}${ccNote} 🍭`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="bg-[#0f0f0f]/95 backdrop-blur-xl border-white/10 text-white max-w-md" data-testid="resend-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Send again to {borrowerName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-white/55">
            We'll re-email this co-branded pre-approval to the borrower. Add anyone else you want to CC.
          </p>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase text-white/50">CC email(s) — optional</Label>
            <Input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="agent@brokerage.com, spouse@email.com"
              className="bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30]"
              data-testid="resend-cc-input"
            />
            <p className="text-[11px] text-white/35">Separate multiple emails with commas.</p>
          </div>
          {showEstimate && pre?.generate_loan_estimate && (
            <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
              <div>
                <p className="text-sm font-medium">Also send the loan summary</p>
                <p className="text-xs text-white/40">Include the loan estimate PDF too</p>
              </div>
              <Switch checked={includeEstimate} onCheckedChange={setIncludeEstimate} data-testid="resend-include-estimate" />
            </div>
          )}
          <Button
            onClick={send}
            disabled={busy}
            data-testid="resend-send-button"
            className="w-full h-12 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send again</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SendEstimateDialog({ pre, borrowerEmail, open, onClose, onSent }) {
  const [cc, setCc] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setCc("");
  }, [open]);
  if (!pre) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/preapprovals/${pre.id}/send-loan-estimate`, { cc_emails: parseCc(cc) });
      toast.success(`Loan estimate sent to ${r.data.to}`);
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send the loan estimate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="bg-[#0f0f0f]/95 backdrop-blur-xl border-white/10 text-white max-w-md" data-testid="send-estimate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">{pre.loan_estimate_shared ? "Resend" : "Send"} loan estimate</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-white/60 -mt-1">
          This emails the loan estimate to <span className="text-white">{borrowerEmail || "the borrower"}</span> and makes it visible on their pre-approval link. Only you (the lender) can send it.
        </p>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-white/50">CC email(s) — optional</Label>
          <Input
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            placeholder="agent@brokerage.com, spouse@email.com"
            className="mt-2 bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-emerald-500"
            data-testid="send-estimate-cc"
          />
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="h-11 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl px-5">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="send-estimate-confirm" className="h-11 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-xl px-5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `${pre.loan_estimate_shared ? "Resend" : "Send"} loan estimate`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientLedger({ clientId, borrowerName, borrowerEmail, borrowerPhone, version, showEstimate = true, lightningOnly = null }) {
  const [allList, setAllList] = useState(null);
  const [preview, setPreview] = useState(null); // {path, filename, title}
  const [resendPre, setResendPre] = useState(null);
  const [estimatePre, setEstimatePre] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/preapprovals`).then((r) => setAllList(r.data)).catch(() => setAllList([]));
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load, version]);

  if (allList === null)
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-[#ff3b30]" />
      </div>
    );

  const list =
    lightningOnly === null
      ? allList
      : allList.filter((p) => (lightningOnly ? p.lightning : !p.lightning));

  return (
    <div data-testid="preapproval-ledger">
      {lightningOnly === null && (
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-[#ff3b30]" />
          <h2 className="font-display text-2xl tracking-tight">Pre-approval ledger</h2>
          <span className="text-xs text-white/40">({list.length})</span>
        </div>
      )}

      {list.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-2xl p-8 text-center">
          <p className="text-white/45 text-sm">No pre-approvals yet. When one is generated, it'll show here — with preview, download and re-send.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((p, idx) => {
            const rurl = `${window.location.origin}/api/share/${p.id}`;
            const body = `Here is ${borrowerName}'s pre-approval from My Pocket Lender:\n${rurl}`;
            const subject = `${borrowerName}'s Pre-Approval — My Pocket Lender`;
            const phone = (borrowerPhone || "").replace(/[^\d+]/g, "");
            const smsHref = `sms:${phone}?&body=${encodeURIComponent(body)}`;
            const mailHref = `mailto:${borrowerEmail || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            return (
            <div
              key={p.id}
              className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-xl shadow-black/30 rounded-2xl p-5"
              data-testid={`ledger-row-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-lg tracking-tight">{money(p.offer_price)}</p>
                    {idx === 0 && (
                      <span className="text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border border-emerald-400/40 text-emerald-400">
                        Latest
                      </span>
                    )}
                    {p.over_max && (
                      <span className="text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border border-[#ff3b30]/40 text-[#ff3b30]">
                        Over max
                      </span>
                    )}
                    {p.loan_estimate_shared && (
                      <span className="text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border border-emerald-400/40 text-emerald-400" data-testid={`ledger-le-sent-${p.id}`}>
                        Loan estimate sent
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/50 truncate">{p.address}</p>
                  <p className="text-xs text-white/35 mt-1">
                    {moneyCents(p.monthly_payment)}/mo · issued {(p.created_at || "").slice(0, 10)} · expires {p.expires_at}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap gap-2">
                <Button
                  onClick={() => setPreview({ path: `/preapprovals/${p.id}/pdf`, filename: `preapproval-${borrowerName}.pdf`, title: "Pre-approval preview" })}
                  variant="outline"
                  data-testid={`ledger-preview-approval-${p.id}`}
                  className="h-9 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-lg text-xs"
                >
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview approval
                </Button>
                <a
                  href={smsHref}
                  data-testid={`ledger-text-${p.id}`}
                  className="inline-flex items-center h-9 px-3 bg-[#ff3b30] hover:bg-[#ff5147] text-white rounded-lg text-xs font-medium transition-colors ml-auto"
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Text
                </a>
                <a
                  href={mailHref}
                  data-testid={`ledger-email-${p.id}`}
                  className="inline-flex items-center h-9 px-3 border border-white/20 text-white hover:bg-white/10 rounded-lg text-xs font-medium transition-colors"
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
                </a>
                <Button
                  onClick={() => setResendPre(p)}
                  data-testid={`ledger-resend-${p.id}`}
                  variant="outline"
                  className="h-9 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-lg text-xs"
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Send again
                </Button>
                {showEstimate && (
                  <Button
                    onClick={() => setEstimatePre(p)}
                    data-testid={`ledger-send-estimate-${p.id}`}
                    className="h-9 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg text-xs"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> {p.loan_estimate_shared ? "Resend loan estimate" : "Send loan estimate"}
                  </Button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      <PdfPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        path={preview?.path}
        filename={preview?.filename}
        title={preview?.title}
      />
      <ResendDialog
        pre={resendPre}
        borrowerName={borrowerName}
        open={!!resendPre}
        onClose={() => setResendPre(null)}
        showEstimate={showEstimate}
      />
      <SendEstimateDialog
        pre={estimatePre}
        borrowerEmail={borrowerEmail}
        open={!!estimatePre}
        onClose={() => setEstimatePre(null)}
        onSent={load}
      />
    </div>
  );
}
